import type { ReactNode } from 'react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import type { BlockGroup } from '@/types/workflow';
import { useTranslation } from '@/i18n/LanguageContext';
import { dispatchOnboardingEvent } from '@/components/onboarding/uiAdapter';
import { useEntityDetailsDialog } from '@/contexts/useEntityDetailsDialog';

interface Props {
  blockId: string;
  blockName?: string;
  children: ReactNode;
  availableGroups: BlockGroup[];
  selectionIds: string[];
  onFocusProperty: (field: 'name' | 'description' | 'color') => void;
  onCreateGroupFromSelection?: (blockIds: string[]) => void;
  onAddToGroup?: (groupId: string, blockId: string) => void;
  onConvertToLayer?: (blockId: string) => void;
  onDuplicate?: (blockId: string) => void;
  onDelete?: (blockId: string) => void;
  /**
   * I-101: öppnar ExplainBlockPanel som låter AI:n förklara blocket.
   * Optional — om inte satt visas menupunkten inte.
   */
  onExplain?: (blockId: string) => void;
  disabled?: boolean;
}

/**
 * Wraps a WorkflowBlock with a right-click context menu. Menu items delegate
 * to handlers from WorkflowCanvas / Index.tsx. Selecting any item dispatches
 * `block:contextActionSelected` with the action name so the onboarding tour
 * can advance on menu-item selection (not just on the right-click gesture).
 */
export function BlockContextMenu({
  blockId,
  blockName,
  children,
  availableGroups,
  selectionIds,
  onFocusProperty,
  onCreateGroupFromSelection,
  onAddToGroup,
  onConvertToLayer,
  onDuplicate,
  onDelete,
  onExplain,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const { openDetails } = useEntityDetailsDialog();

  if (disabled) return <>{children}</>;

  const multi = selectionIds.length >= 2 && selectionIds.includes(blockId);
  const groupTargetIds = multi ? selectionIds : [blockId];
  const groupsWithoutThis = availableGroups.filter(
    (g) => !g.blockIds.includes(blockId),
  );

  // Helper: wrap onSelect so every menu click also dispatches a tour event
  // identified by the item's data-guide action name. The tour can then
  // advance on a specific action (advanceOn: { kind: 'menuItemSelected',
  // action: 'ctx-group' }) or on any action (no `action`).
  const withDispatch = (action: string, fn: () => void) => () => {
    dispatchOnboardingEvent({ name: 'block:contextActionSelected', target: action });
    fn();
  };

  return (
    <ContextMenu>
      {/*
        Wrap in a native <div> so Radix's Slot has a real DOM node to attach
        its cloned ref + onContextMenu handler to. We can't asChild directly
        on <WorkflowBlock> because it's a memoized component whose root div
        doesn't forward arbitrary props.
      */}
      <ContextMenuTrigger asChild>
        <div
          onContextMenu={() => {
            dispatchOnboardingEvent({ name: 'block:rightclick', target: blockId });
          }}
        >
          {children}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        <ContextMenuItem
          data-guide="ctx-rename"
          onSelect={withDispatch('ctx-rename', () => onFocusProperty('name'))}
        >
          {t('editName') || t('rename') || 'Edit name'}
        </ContextMenuItem>
        <ContextMenuItem
          data-guide="ctx-description"
          onSelect={withDispatch('ctx-description', () => onFocusProperty('description'))}
        >
          {t('editDescription') || 'Edit description'}
        </ContextMenuItem>
        <ContextMenuItem
          data-guide="ctx-color"
          onSelect={withDispatch('ctx-color', () => onFocusProperty('color'))}
        >
          {t('editColor') || t('changeColor') || 'Edit color'}
        </ContextMenuItem>
        <ContextMenuItem
          data-guide="ctx-details"
          onSelect={withDispatch('ctx-details', () => openDetails({
            entityId: blockId, entityType: 'block', entityName: blockName,
          }))}
        >
          {t('details') || 'Details…'}
        </ContextMenuItem>

        <ContextMenuSeparator />

        {onCreateGroupFromSelection && (
          <ContextMenuItem
            data-guide="ctx-group"
            onSelect={withDispatch('ctx-group', () => onCreateGroupFromSelection(groupTargetIds))}
          >
            {multi
              ? (t('createGroupFromSelection') || `Group ${groupTargetIds.length} blocks`)
              : (t('groupThisBlock') || 'Group this block')}
          </ContextMenuItem>
        )}

        {onAddToGroup && groupsWithoutThis.length > 0 && (
          <ContextMenuSub>
            <ContextMenuSubTrigger data-guide="ctx-add-to-group">
              {t('addToGroup') || 'Add to group'}
            </ContextMenuSubTrigger>
            <ContextMenuSubContent className="w-48">
              {groupsWithoutThis.map((g) => (
                <ContextMenuItem
                  key={g.id}
                  onSelect={withDispatch('ctx-add-to-group', () => onAddToGroup(g.id, blockId))}
                >
                  <span
                    aria-hidden="true"
                    className="inline-block w-3 h-3 rounded-sm mr-2"
                    style={{ backgroundColor: g.color }}
                  />
                  {g.name || 'Unnamed group'}
                </ContextMenuItem>
              ))}
            </ContextMenuSubContent>
          </ContextMenuSub>
        )}

        {onConvertToLayer && (
          <ContextMenuItem
            data-guide="ctx-convert-to-layer"
            onSelect={withDispatch('ctx-convert-to-layer', () => onConvertToLayer(blockId))}
          >
            {t('convertToLayer') || 'Convert to layer'}
          </ContextMenuItem>
        )}

        <ContextMenuSeparator />

        {onExplain && (
          <ContextMenuItem
            data-guide="ctx-explain"
            onSelect={withDispatch('ctx-explain', () => onExplain(blockId))}
          >
            {t('explainBlock') || 'Förklara block…'}
          </ContextMenuItem>
        )}
        {onDuplicate && (
          <ContextMenuItem
            data-guide="ctx-duplicate"
            onSelect={withDispatch('ctx-duplicate', () => onDuplicate(blockId))}
          >
            {t('duplicate') || 'Duplicate'}
          </ContextMenuItem>
        )}
        {onDelete && (
          <ContextMenuItem
            data-guide="ctx-delete"
            onSelect={withDispatch('ctx-delete', () => onDelete(blockId))}
            className="text-destructive focus:text-destructive"
          >
            {t('delete') || 'Delete'}
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
